// src/api/mock/recepcionDigital.fixtures.js
//
// ── Design decisions ──────────────────────────────────────────────────────────
// Fixtures for the Recepción Digital module. 14 records, mapping 1:1 (in id
// order) to the 14 required edge cases of the build brief — see EDGE_CASE_INDEX
// at the bottom for the authoritative mapping used by tests and the session
// report.
//
// - makeFixtures() is a FUNCTION, not a constant: every call rebuilds the
//   array from literals so callers always get fresh, unshared objects. The
//   mock API layer reseeds from it (resetMockData) without cross-test bleed.
// - Timestamps are computed relative to Date.now() via minutesAgo()/daysAgo()
//   so "elapsed since last activity" chips and the 14-day expiry case stay
//   truthful no matter when the app runs.
// - The DAI tier follows calcPackageTax in src/utils/customsCalc.js, per
//   package: R = FOB × 1.10; DAI only when R > threshold_usd (200). Only
//   enc-008 crosses it.
// - Item descriptions are stored UPPERCASE and capped at 60 chars, matching
//   generateAnexoPDF.js storage rules (enc-013 exercises the truncation).
// - All client-facing text is Salvadoran Spanish, written as real WhatsApp
//   chat (typos, "vaya pues", voice notes) — the thread IS the source of
//   truth the operator reads, so it must look like the real thing.
// - media_url values are local placeholder paths, never real URLs.

const MIN = 60 * 1000;
const DAY = 24 * 60 * MIN;

export const minutesAgo = (n) => new Date(Date.now() - n * MIN).toISOString();
export const daysAgo    = (n) => new Date(Date.now() - n * DAY).toISOString();

// Shared snapshot — sourced from customs_config defaults (read-only header strip)
const SNAPSHOT = () => ({
  registro_prefix: '4-',
  aduana_code:     '03',
  gestor_code:     'GE2662',
  gestor_name:     'JOSE ROBERTO MENJIVAR',
  threshold_usd:   200,
  flete_rate:      0.10,
  seguro_rate:     0,
  dai_rate:        0.19,
  iva_rate:        0.13,
});

// enc-013: original client phrasing exceeds 60 chars → stored slice(0, 60)
const LONG_DESC_FULL =
  'JUEGO DE HERRAMIENTAS ELECTRICAS CON TALADRO INALAMBRICO, BROCAS DE REPUESTO Y ESTUCHE';

// ── Fixture builder ───────────────────────────────────────────────────────────

export function makeFixtures() {
  return [

    // ── enc-001 — lista_para_producción, hilo limpio, 3 paquetes ─────────────
    {
      id: 'enc-001',
      client: { name: 'María Argueta', phone: '+1 (301) 555-0142', is_encomendero: true },
      tenant_id: 'aviance',
      status: 'lista_para_producción',
      whatsapp_thread: [
        { id: 'wa-001-1', from: 'client', text: 'Buenas! Le mando la lista de esta semana. Van 3 paquetes.', timestamp: minutesAgo(95) },
        { id: 'wa-001-2', from: 'client', text: 'Para Rosa Menjívar en Soyapango: 4 camisas nuevas a $8 cada una y 2 pares de zapatos nuevos a $25.', timestamp: minutesAgo(94), is_source_for_items: ['it-001-1', 'it-001-2'] },
        { id: 'wa-001-3', from: 'client', text: 'Para Carlos Rivas en Santa Ana: 1 licuadora nueva de $35 y 6 desodorantes a $4.', timestamp: minutesAgo(92), is_source_for_items: ['it-001-3', 'it-001-4'] },
        { id: 'wa-001-4', from: 'client', text: 'Y para Doris López en Mejicanos: 10 pastillas para el dolor OTC a $3 y 3 cremas a $5. Todo nuevo.', timestamp: minutesAgo(90), is_source_for_items: ['it-001-5', 'it-001-6'] },
        { id: 'wa-001-5', from: 'agent', text: 'Recibido María, quedó registrada su encomienda con 3 paquetes. Le avisamos cualquier consulta.', timestamp: minutesAgo(89) },
      ],
      packages: [
        {
          unique_package_id: 'AV-101',
          recipient_name: 'Rosa Menjívar',
          recipient_address_hint: 'Soyapango, cerca del mercado, tel +503 7845-2210',
          taxes: { dai: 0 },
          items: [
            { id: 'it-001-1', description: 'CAMISAS DE VESTIR', quantity: 4, unit_value: 8, condition: 'NUEVO', source_message_id: 'wa-001-2' },
            { id: 'it-001-2', description: 'ZAPATOS CASUALES PAR', quantity: 2, unit_value: 25, condition: 'NUEVO', source_message_id: 'wa-001-2' },
          ],
        },
        {
          unique_package_id: 'AV-102',
          recipient_name: 'Carlos Rivas',
          recipient_address_hint: 'Santa Ana, col. El Palmar, tel +503 7712-9034',
          taxes: { dai: 0 },
          items: [
            { id: 'it-001-3', description: 'LICUADORA', quantity: 1, unit_value: 35, condition: 'NUEVO', source_message_id: 'wa-001-3' },
            { id: 'it-001-4', description: 'DESODORANTES', quantity: 6, unit_value: 4, condition: 'NUEVO', source_message_id: 'wa-001-3' },
          ],
        },
        {
          unique_package_id: 'AV-103',
          recipient_name: 'Doris López',
          recipient_address_hint: 'Mejicanos, tel +503 7290-4451',
          taxes: { dai: 0 },
          items: [
            { id: 'it-001-5', description: 'PASTILLAS PARA DOLOR OTC', quantity: 10, unit_value: 3, condition: 'NUEVO', source_message_id: 'wa-001-4' },
            { id: 'it-001-6', description: 'CREMAS CORPORALES', quantity: 3, unit_value: 5, condition: 'NUEVO', source_message_id: 'wa-001-4' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(90),
      updated_at: minutesAgo(89),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-002 — necesita_revisión, el cliente se contradice (3 vs 4 pares) ─
    {
      id: 'enc-002',
      client: { name: 'José Hernández', phone: '+1 (713) 555-0188', is_encomendero: true },
      tenant_id: 'aviance',
      status: 'necesita_revisión',
      whatsapp_thread: [
        { id: 'wa-002-1', from: 'client', text: 'Buenas tardes, va una caja para mi hermana Wendy en San Miguel.', timestamp: minutesAgo(240) },
        { id: 'wa-002-2', from: 'client', text: 'Lleva 3 pares de zapatos nuevos, a 30 dolares el par.', timestamp: minutesAgo(238), is_source_for_items: ['it-002-1'] },
        { id: 'wa-002-3', from: 'client', text: 'ah y unas 5 camisetas usadas q ya no uso, pongale $2 cada una', timestamp: minutesAgo(236), is_source_for_items: ['it-002-2'] },
        { id: 'wa-002-4', from: 'client', text: 'perdon, en realidad son 4 pares de zapatos, conté mal jaja', timestamp: minutesAgo(230) },
      ],
      packages: [
        {
          unique_package_id: 'AV-104',
          recipient_name: 'Wendy Hernández',
          recipient_address_hint: 'San Miguel, col. Ciudad Jardín, tel +503 7601-8823',
          taxes: { dai: 0 },
          items: [
            { id: 'it-002-1', description: 'ZAPATOS DEPORTIVOS PAR', quantity: 3, unit_value: 30, condition: 'NUEVO', source_message_id: 'wa-002-2' },
            { id: 'it-002-2', description: 'CAMISETAS', quantity: 5, unit_value: 2, condition: 'USADO', source_message_id: 'wa-002-3' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(230),
      updated_at: minutesAgo(228),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-003 — esperando_cliente, se preguntó ¿nuevo o usado? sin respuesta
    {
      id: 'enc-003',
      client: { name: 'Roxana Portillo', phone: '+1 (516) 555-0107', is_encomendero: false },
      tenant_id: 'aviance',
      status: 'esperando_cliente',
      whatsapp_thread: [
        { id: 'wa-003-1', from: 'client', text: 'Hola, quiero mandar una plancha de pelo y 2 perfumes para mi mamá en Sonsonate.', timestamp: minutesAgo(1500), is_source_for_items: ['it-003-1', 'it-003-2'] },
        { id: 'wa-003-2', from: 'client', text: 'la plancha vale como $40 y los perfumes $22 cada uno', timestamp: minutesAgo(1498) },
        { id: 'wa-003-3', from: 'agent', text: 'Confirmame por favor: la plancha de pelo, ¿es nueva o usada?', timestamp: minutesAgo(1440) },
      ],
      packages: [
        {
          unique_package_id: 'AV-105',
          recipient_name: 'Blanca de Portillo',
          recipient_address_hint: 'Sonsonate centro, tel +503 7188-3467',
          taxes: { dai: 0 },
          items: [
            { id: 'it-003-1', description: 'PLANCHA DE PELO', quantity: 1, unit_value: 40, condition: 'NUEVO', source_message_id: 'wa-003-1' },
            { id: 'it-003-2', description: 'PERFUMES', quantity: 2, unit_value: 22, condition: 'NUEVO', source_message_id: 'wa-003-1' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(1498),
      updated_at: minutesAgo(1440),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-004 — pendiente_llegada, hilo iniciado, aún sin destinatarios ────
    {
      id: 'enc-004',
      client: { name: 'Cristian Alvarado', phone: '+1 (202) 555-0173', is_encomendero: true },
      tenant_id: 'aviance',
      status: 'pendiente_llegada',
      whatsapp_thread: [
        { id: 'wa-004-1', from: 'client', text: 'Buenas, la otra semana les mando encomienda, como 4 cajas', timestamp: minutesAgo(45) },
        { id: 'wa-004-2', from: 'agent', text: 'Con gusto Cristian, cuando tenga la lista de destinatarios nos la comparte por aquí.', timestamp: minutesAgo(44) },
        { id: 'wa-004-3', from: 'client', text: 'vaya pues, ahorita ando recogiendo los encargos todavia', timestamp: minutesAgo(40) },
      ],
      packages: [],
      last_client_activity_at: minutesAgo(40),
      updated_at: minutesAgo(40),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-005 — expirada, 15 días sin actividad ────────────────────────────
    {
      id: 'enc-005',
      client: { name: 'Marvin Quintanilla', phone: '+1 (617) 555-0129', is_encomendero: false },
      tenant_id: 'aviance',
      status: 'expirada',
      whatsapp_thread: [
        { id: 'wa-005-1', from: 'client', text: 'quiero mandar unas cositas para La Libertad, luego les paso la lista', timestamp: daysAgo(15) },
        { id: 'wa-005-2', from: 'agent', text: 'Perfecto Marvin, aquí quedamos pendientes de su lista.', timestamp: daysAgo(15) },
      ],
      packages: [],
      last_client_activity_at: daysAgo(15),
      updated_at: daysAgo(15),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-006 — encomendero con 5 paquetes / 5 destinatarios ───────────────
    {
      id: 'enc-006',
      client: { name: 'Doris Campos', phone: '+1 (818) 555-0164', is_encomendero: true },
      tenant_id: 'aviance',
      status: 'lista_para_producción',
      whatsapp_thread: [
        { id: 'wa-006-1', from: 'client', text: 'Lista de la semana, van 5 paquetes, todo nuevo salvo lo que le marco.', timestamp: minutesAgo(300) },
        { id: 'wa-006-2', from: 'client', text: 'Ana Fuentes, Soyapango: 6 pantalones a $12. Pedro Miranda, Ahuachapán: 1 taladro de $45 y 2 martillos a $10.', timestamp: minutesAgo(298), is_source_for_items: ['it-006-1', 'it-006-2', 'it-006-3'] },
        { id: 'wa-006-3', from: 'client', text: 'Sonia Deras, Santa Ana: 8 blusas a $6. Rodrigo Peña, San Miguel: 1 microondas usado de $30.', timestamp: minutesAgo(296), is_source_for_items: ['it-006-4', 'it-006-5'] },
        { id: 'wa-006-4', from: 'client', text: 'Y Julia Ortez, Mejicanos: 12 shampoos a $4 y 6 cepillos de dientes a $1.50', timestamp: minutesAgo(294), is_source_for_items: ['it-006-6', 'it-006-7'] },
        { id: 'wa-006-5', from: 'agent', text: 'Recibido Doris, 5 paquetes registrados. Cualquier duda le escribimos por aquí.', timestamp: minutesAgo(292) },
      ],
      packages: [
        {
          unique_package_id: 'AV-106',
          recipient_name: 'Ana Fuentes',
          recipient_address_hint: 'Soyapango, tel +503 7933-5182',
          taxes: { dai: 0 },
          items: [
            { id: 'it-006-1', description: 'PANTALONES', quantity: 6, unit_value: 12, condition: 'NUEVO', source_message_id: 'wa-006-2' },
          ],
        },
        {
          unique_package_id: 'AV-107',
          recipient_name: 'Pedro Miranda',
          recipient_address_hint: 'Ahuachapán, barrio San José, tel +503 7521-6640',
          taxes: { dai: 0 },
          items: [
            { id: 'it-006-2', description: 'TALADRO ELECTRICO', quantity: 1, unit_value: 45, condition: 'NUEVO', source_message_id: 'wa-006-2' },
            { id: 'it-006-3', description: 'MARTILLOS', quantity: 2, unit_value: 10, condition: 'NUEVO', source_message_id: 'wa-006-2' },
          ],
        },
        {
          unique_package_id: 'AV-108',
          recipient_name: 'Sonia Deras',
          recipient_address_hint: 'Santa Ana, tel +503 7407-2915',
          taxes: { dai: 0 },
          items: [
            { id: 'it-006-4', description: 'BLUSAS', quantity: 8, unit_value: 6, condition: 'NUEVO', source_message_id: 'wa-006-3' },
          ],
        },
        {
          unique_package_id: 'AV-109',
          recipient_name: 'Rodrigo Peña',
          recipient_address_hint: 'San Miguel, tel +503 7614-8098',
          taxes: { dai: 0 },
          items: [
            { id: 'it-006-5', description: 'MICROONDAS', quantity: 1, unit_value: 30, condition: 'USADO', source_message_id: 'wa-006-3' },
          ],
        },
        {
          unique_package_id: 'AV-110',
          recipient_name: 'Julia Ortez',
          recipient_address_hint: 'Mejicanos, tel +503 7842-1173',
          taxes: { dai: 0 },
          items: [
            { id: 'it-006-6', description: 'SHAMPOOS', quantity: 12, unit_value: 4, condition: 'NUEVO', source_message_id: 'wa-006-4' },
            { id: 'it-006-7', description: 'CEPILLOS DE DIENTES', quantity: 6, unit_value: 1.5, condition: 'NUEVO', source_message_id: 'wa-006-4' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(294),
      updated_at: minutesAgo(292),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-007 — viajera con un solo paquete (la UI colapsa con gracia) ─────
    {
      id: 'enc-007',
      client: { name: 'Wendy Melgar', phone: '+1 (404) 555-0151', is_encomendero: false },
      tenant_id: 'latia',
      status: 'lista_para_producción',
      whatsapp_thread: [
        { id: 'wa-007-1', from: 'client', text: 'Hola! Viajo el sábado y llevo una caja para mi tía en Santa Ana.', timestamp: minutesAgo(600) },
        { id: 'wa-007-2', from: 'client', text: 'Lleva 3 vestidos nuevos a $15, 2 pares de sandalias a $12 y un set de maquillaje de $25.', timestamp: minutesAgo(598), is_source_for_items: ['it-007-1', 'it-007-2', 'it-007-3'] },
        { id: 'wa-007-3', from: 'agent', text: 'Perfecto Wendy, quedó registrado su paquete. Buen viaje.', timestamp: minutesAgo(595) },
      ],
      packages: [
        {
          unique_package_id: 'LT-201',
          recipient_name: 'Gloria Melgar',
          recipient_address_hint: 'Santa Ana, col. IVU, tel +503 7350-6629',
          taxes: { dai: 0 },
          items: [
            { id: 'it-007-1', description: 'VESTIDOS', quantity: 3, unit_value: 15, condition: 'NUEVO', source_message_id: 'wa-007-2' },
            { id: 'it-007-2', description: 'SANDALIAS PAR', quantity: 2, unit_value: 12, condition: 'NUEVO', source_message_id: 'wa-007-2' },
            { id: 'it-007-3', description: 'SET DE MAQUILLAJE', quantity: 1, unit_value: 25, condition: 'NUEVO', source_message_id: 'wa-007-2' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(598),
      updated_at: minutesAgo(595),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-008 — cruza el umbral: FOB $185 → CIF $214.51 > $200 → DAI 30% ───
    {
      id: 'enc-008',
      client: { name: 'Rodrigo Ayala', phone: '+1 (240) 555-0136', is_encomendero: false },
      tenant_id: 'aviance',
      status: 'necesita_revisión',
      whatsapp_thread: [
        { id: 'wa-008-1', from: 'client', text: 'Quiero mandarle a mi papá en La Libertad un televisor pequeño nuevo, me costó $120.', timestamp: minutesAgo(180), is_source_for_items: ['it-008-1'] },
        { id: 'wa-008-2', from: 'client', text: 'También van 2 tenis nuevos de $20 cada uno y una tablet usada que vale como $25.', timestamp: minutesAgo(178), is_source_for_items: ['it-008-2', 'it-008-3'] },
        { id: 'wa-008-3', from: 'agent', text: 'Recibido Rodrigo. Le aviso que por el valor declarado este envío puede pagar impuesto del 30%.', timestamp: minutesAgo(175) },
      ],
      packages: [
        {
          unique_package_id: 'AV-111',
          recipient_name: 'Ernesto Ayala',
          recipient_address_hint: 'Puerto de La Libertad, tel +503 7266-9083',
          // FOB 185 → R 185 × 1.10 = 203.50 > 200 → paga DAI
          taxes: { dai: 43.69 },
          items: [
            { id: 'it-008-1', description: 'TELEVISOR 24 PULGADAS', quantity: 1, unit_value: 120, condition: 'NUEVO', source_message_id: 'wa-008-1' },
            { id: 'it-008-2', description: 'TENIS PAR', quantity: 2, unit_value: 20, condition: 'NUEVO', source_message_id: 'wa-008-2' },
            { id: 'it-008-3', description: 'TABLET', quantity: 1, unit_value: 25, condition: 'USADO', source_message_id: 'wa-008-2' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(178),
      updated_at: minutesAgo(175),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-009 — mensaje de nota de voz (stub de audio con duración) ────────
    {
      id: 'enc-009',
      client: { name: 'María Escobar', phone: '+1 (972) 555-0119', is_encomendero: true },
      tenant_id: 'aviance',
      status: 'necesita_revisión',
      whatsapp_thread: [
        { id: 'wa-009-1', from: 'client', text: 'le mando audio con la lista pq ando manejando', timestamp: minutesAgo(120) },
        { id: 'wa-009-2', from: 'client', text: '', media_type: 'audio', media_url: '/mock/voicenote-enc-009.ogg', duration_seconds: 47, timestamp: minutesAgo(119) },
        { id: 'wa-009-3', from: 'agent', text: 'Escuchamos su nota de voz. Registramos: 2 sartenes nuevos a $18 y 4 toallas a $7. Confirmame si es correcto.', timestamp: minutesAgo(110), is_source_for_items: ['it-009-1', 'it-009-2'] },
      ],
      packages: [
        {
          unique_package_id: 'AV-112',
          recipient_name: 'Teresa Escobar',
          recipient_address_hint: 'Soyapango, col. Las Margaritas, tel +503 7098-4416',
          taxes: { dai: 0 },
          items: [
            { id: 'it-009-1', description: 'SARTENES', quantity: 2, unit_value: 18, condition: 'NUEVO', source_message_id: 'wa-009-2' },
            { id: 'it-009-2', description: 'TOALLAS', quantity: 4, unit_value: 7, condition: 'NUEVO', source_message_id: 'wa-009-2' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(119),
      updated_at: minutesAgo(110),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-010 — foto con recibo visible (preview inline) ───────────────────
    {
      id: 'enc-010',
      client: { name: 'José Ventura', phone: '+1 (571) 555-0195', is_encomendero: false },
      tenant_id: 'latia',
      status: 'lista_para_producción',
      whatsapp_thread: [
        { id: 'wa-010-1', from: 'client', text: 'ahi le mando la foto del recibo de lo que compré', timestamp: minutesAgo(360) },
        { id: 'wa-010-2', from: 'client', text: '', media_type: 'image', media_url: '/mock/receipt-enc-010.jpg', timestamp: minutesAgo(359), is_source_for_items: ['it-010-1', 'it-010-2'] },
        { id: 'wa-010-3', from: 'client', text: 'es para mi hermano Óscar en Sonsonate, todo nuevo', timestamp: minutesAgo(358) },
        { id: 'wa-010-4', from: 'agent', text: 'Recibido José, tomamos los artículos del recibo de la foto.', timestamp: minutesAgo(350) },
      ],
      packages: [
        {
          unique_package_id: 'LT-202',
          recipient_name: 'Óscar Ventura',
          recipient_address_hint: 'Sonsonate, barrio El Ángel, tel +503 7773-2054',
          taxes: { dai: 0 },
          items: [
            { id: 'it-010-1', description: 'AUDIFONOS BLUETOOTH', quantity: 2, unit_value: 22, condition: 'NUEVO', source_message_id: 'wa-010-2' },
            { id: 'it-010-2', description: 'CARGADORES DE CELULAR', quantity: 3, unit_value: 9, condition: 'NUEVO', source_message_id: 'wa-010-2' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(358),
      updated_at: minutesAgo(350),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-011 — un mensaje citado como fuente de DOS ítems (proveniencia
    //    compartida entre paquetes distintos) ──────────────────────────────────
    {
      id: 'enc-011',
      client: { name: 'Cristian Bonilla', phone: '+1 (703) 555-0122', is_encomendero: true },
      tenant_id: 'aviance',
      status: 'lista_para_producción',
      whatsapp_thread: [
        { id: 'wa-011-1', from: 'client', text: 'Van 2 cajas: para Elena Cortez en Ahuachapán y para Mario Cortez en Santa Ana. A CADA UNO le va 1 par de botas nuevas de $35.', timestamp: minutesAgo(420), is_source_for_items: ['it-011-1', 'it-011-2'] },
        { id: 'wa-011-2', from: 'client', text: 'a Elena tambien le van 3 sabanas a $10', timestamp: minutesAgo(418), is_source_for_items: ['it-011-3'] },
        { id: 'wa-011-3', from: 'agent', text: 'Registrado Cristian: botas para ambos destinatarios y sábanas para Elena.', timestamp: minutesAgo(415) },
      ],
      packages: [
        {
          unique_package_id: 'AV-113',
          recipient_name: 'Elena Cortez',
          recipient_address_hint: 'Ahuachapán centro, tel +503 7509-7731',
          taxes: { dai: 0 },
          items: [
            { id: 'it-011-1', description: 'BOTAS PAR', quantity: 1, unit_value: 35, condition: 'NUEVO', source_message_id: 'wa-011-1' },
            { id: 'it-011-3', description: 'SABANAS', quantity: 3, unit_value: 10, condition: 'NUEVO', source_message_id: 'wa-011-2' },
          ],
        },
        {
          unique_package_id: 'AV-114',
          recipient_name: 'Mario Cortez',
          recipient_address_hint: 'Santa Ana, col. Santa Lucía, tel +503 7645-0817',
          taxes: { dai: 0 },
          items: [
            { id: 'it-011-2', description: 'BOTAS PAR', quantity: 1, unit_value: 35, condition: 'NUEVO', source_message_id: 'wa-011-1' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(418),
      updated_at: minutesAgo(415),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-012 — ítem agregado manualmente por el operador (sin referencia
    //    del cliente): no_client_reference = true, sin source_message_id ──────
    {
      id: 'enc-012',
      client: { name: 'Marvin Sorto', phone: '+1 (305) 555-0148', is_encomendero: true },
      tenant_id: 'aviance',
      status: 'necesita_revisión',
      whatsapp_thread: [
        { id: 'wa-012-1', from: 'client', text: 'Para Rafael Guardado en San Miguel: 5 gorras nuevas a $8', timestamp: minutesAgo(200), is_source_for_items: ['it-012-1'] },
        { id: 'wa-012-2', from: 'agent', text: 'Registrado Marvin. Vimos en bodega que la caja también trae un cinturón — lo agregamos con valor estimado, confirmenos por favor.', timestamp: minutesAgo(190) },
      ],
      packages: [
        {
          unique_package_id: 'AV-115',
          recipient_name: 'Rafael Guardado',
          recipient_address_hint: 'San Miguel, col. Milagro de la Paz, tel +503 7530-2298',
          taxes: { dai: 0 },
          items: [
            { id: 'it-012-1', description: 'GORRAS', quantity: 5, unit_value: 8, condition: 'NUEVO', source_message_id: 'wa-012-1' },
            { id: 'it-012-2', description: 'CINTURON DE CUERO', quantity: 1, unit_value: 12, condition: 'NUEVO', no_client_reference: true },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(200),
      updated_at: minutesAgo(190),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-013 — descripción original > 60 chars → almacenada en 60 exactos ─
    {
      id: 'enc-013',
      client: { name: 'Roxana Deras', phone: '+1 (301) 555-0177', is_encomendero: false },
      tenant_id: 'latia',
      status: 'lista_para_producción',
      whatsapp_thread: [
        { id: 'wa-013-1', from: 'client', text: 'Le mando a mi esposo un juego de herramientas eléctricas con taladro inalámbrico, brocas de repuesto y estuche. Me costó $95, nuevo.', timestamp: minutesAgo(700), is_source_for_items: ['it-013-1'] },
        { id: 'wa-013-2', from: 'agent', text: 'Registrado Roxana. Nota: la descripción se recorta a 60 caracteres para el Anexo.', timestamp: minutesAgo(695) },
      ],
      packages: [
        {
          unique_package_id: 'LT-203',
          recipient_name: 'Manuel Deras',
          recipient_address_hint: 'La Libertad, col. San Rafael, tel +503 7457-6690',
          taxes: { dai: 0 },
          items: [
            // Stored form = full phrasing recortada a exactamente 60 caracteres
            { id: 'it-013-1', description: LONG_DESC_FULL.toUpperCase().slice(0, 60), quantity: 1, unit_value: 95, condition: 'NUEVO', source_message_id: 'wa-013-1' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(700),
      updated_at: minutesAgo(695),
      customs_config_snapshot: SNAPSHOT(),
    },

    // ── enc-014 — orden por entero final vs alfabético: AV-9 va ANTES de
    //    AV-10 numéricamente aunque 'AV-10' < 'AV-9' alfabéticamente ──────────
    {
      id: 'enc-014',
      client: { name: 'Doris Vásquez', phone: '+1 (646) 555-0113', is_encomendero: true },
      tenant_id: 'aviance',
      status: 'lista_para_producción',
      whatsapp_thread: [
        { id: 'wa-014-1', from: 'client', text: 'Dos cajas: para Karla Rauda en Soyapango van 4 juguetes nuevos a $9, y para Berta Rauda en Santa Ana 2 bolsos nuevos a $14.', timestamp: minutesAgo(80), is_source_for_items: ['it-014-1', 'it-014-2'] },
        { id: 'wa-014-2', from: 'agent', text: 'Registrado Doris, quedaron las dos cajas.', timestamp: minutesAgo(78) },
      ],
      packages: [
        {
          unique_package_id: 'AV-10',
          recipient_name: 'Berta Rauda',
          recipient_address_hint: 'Santa Ana, tel +503 7218-5540',
          taxes: { dai: 0 },
          items: [
            { id: 'it-014-2', description: 'BOLSOS', quantity: 2, unit_value: 14, condition: 'NUEVO', source_message_id: 'wa-014-1' },
          ],
        },
        {
          unique_package_id: 'AV-9',
          recipient_name: 'Karla Rauda',
          recipient_address_hint: 'Soyapango, col. Sierra Morena, tel +503 7381-9902',
          taxes: { dai: 0 },
          items: [
            { id: 'it-014-1', description: 'JUGUETES', quantity: 4, unit_value: 9, condition: 'NUEVO', source_message_id: 'wa-014-1' },
          ],
        },
      ],
      last_client_activity_at: minutesAgo(80),
      updated_at: minutesAgo(78),
      customs_config_snapshot: SNAPSHOT(),
    },

  ];
}

// ── Edge-case index (id → caso que cubre) ─────────────────────────────────────
// Used by tests and the session summary report to assert 14/14 coverage.
export const EDGE_CASE_INDEX = {
  'enc-001': 'Lista para producción con hilo limpio y 3 paquetes',
  'enc-002': 'Necesita revisión: el cliente se contradice ("3 pares" → "en realidad son 4")',
  'enc-003': 'Esperando cliente: se preguntó "¿nuevo o usado?" y no hay respuesta',
  'enc-004': 'Pendiente de llegada: hilo iniciado, destinatarios aún no declarados',
  'enc-005': 'Expirada: más de 14 días sin actividad del cliente',
  'enc-006': 'Encomendero con 5 paquetes / múltiples destinatarios',
  'enc-007': 'Viajera con un solo paquete (la UI colapsa con gracia)',
  'enc-008': 'Cruza el umbral de impuesto: CIF > $200 → nivel 30% (DAI > 0)',
  'enc-009': 'Mensaje de nota de voz (stub de audio con duración)',
  'enc-010': 'Foto con recibo visible (preview inline)',
  'enc-011': 'Un mensaje citado como fuente de dos ítems distintos (proveniencia compartida)',
  'enc-012': 'Ítem agregado manualmente por el operador (no_client_reference, sin mensaje fuente)',
  'enc-013': 'Descripción original mayor a 60 caracteres, almacenada recortada a 60',
  'enc-014': 'Orden de paquetes: entero final (AV-9 antes de AV-10) difiere del orden alfabético',
};
